import { HomeWrap } from "./HomeWrap";

/**
 * The dark anchor section. The dial and the four grade circles are built in
 * HTML and SVG exactly as the design specifies, not a screenshot, so they stay
 * crisp at any width and cost no image bytes.
 *
 * The dial numbers are the design's illustrative example of a score, the same
 * way the old screenshot was. They are not read from the database.
 */
const GRADES = [
  { letter: "A", tone: "a", title: "WiFi", body: "Whether there's WiFi at all, and whether it holds up. Plus speed tests from people who were sitting right there." },
  { letter: "B", tone: "b", title: "Outlets", body: "Plenty, a few by the window, or none at all. The difference between three hours and forty minutes." },
  { letter: "A", tone: "a", title: "Seating", body: "How much there is, from plenty down to almost none. Whether you sit down or turn around." },
  { letter: "C", tone: "c", title: "Parking", body: "Free lot, street, metered or nothing. The thing every other list leaves out, and the reason you circle the block." },
] as const;

const TONE: Record<string, string> = {
  a: "text-[#8FD3A4] border-[rgba(143,211,164,.5)] bg-[rgba(143,211,164,.09)]",
  b: "text-[#E4C76B] border-[rgba(228,199,107,.5)] bg-[rgba(228,199,107,.09)]",
  c: "text-[#D9A183] border-[rgba(217,161,131,.5)] bg-[rgba(217,161,131,.09)]",
};

export function HomeScore() {
  return (
    <section id="score" className="scroll-mt-20 bg-[#1C130C] py-[92px] pb-24 text-[#F6EFE4] max-[820px]:py-16 max-[820px]:pb-[68px]">
      <HomeWrap>
        <h2 className="max-w-[16ch] font-display text-[clamp(34px,4.8vw,54px)] font-semibold leading-[1.04] tracking-[-0.015em]">
          Same four questions. Every café. Every time.
        </h2>
        <p className="mt-[22px] max-w-[52ch] text-[18.5px] leading-[1.6] text-[#BFA88E]">
          Every café gets a score out of 100, built from those four things and nothing else. Not
          vibes, not stars for the latte. Whether you can sit down and get your work done.
        </p>

        <div className="mt-14 grid items-center gap-[52px] min-[900px]:grid-cols-[.9fr_1.1fr] min-[900px]:gap-[72px]">
          <div className="relative mx-auto h-[260px] w-[260px]">
            <svg viewBox="0 0 260 260" width="260" height="260" className="absolute inset-0" aria-hidden="true">
              <circle cx="130" cy="130" r="118" fill="none" stroke="rgba(246,239,228,.13)" strokeWidth="10" />
              <circle
                cx="130" cy="130" r="118" fill="none" stroke="#C9A227" strokeWidth="10"
                strokeLinecap="round" strokeDasharray="741" strokeDashoffset="111"
                transform="rotate(-90 130 130)"
              />
            </svg>
            <div data-centered-ok className="absolute inset-0 grid place-items-center text-center">
              <div>
                <b className="block font-display text-[96px] font-semibold leading-none tracking-[-.03em]">85</b>
                <i className="mt-2 block text-[11px] not-italic uppercase tracking-[.2em] text-[#BFA88E]">
                  Grounded score
                </i>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-[34px] gap-y-[26px] max-[560px]:grid-cols-1">
            {GRADES.map((g) => (
              <div key={g.title} className="flex items-start gap-4">
                <div className={`grid h-12 w-12 flex-none place-items-center rounded-full border-[1.5px] font-display text-2xl font-semibold ${TONE[g.tone]}`}>
                  {g.letter}
                </div>
                <div>
                  <h3 className="mb-[3px] font-display text-xl font-semibold text-[#F6EFE4]">{g.title}</h3>
                  <p className="text-[14.5px] leading-[1.5] text-[#BFA88E]">{g.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-14 max-w-[58ch] border-t border-[rgba(246,239,228,.14)] pt-[26px] text-[16.5px] leading-[1.6] text-[#BFA88E]">
          Every café is measured the same way, so the number is worth sorting by. Filter for what you
          need today and it means the same thing everywhere on the list.
        </p>
      </HomeWrap>
    </section>
  );
}
